"use client";

import { useState } from "react";
import { createVouch } from "@/app/actions/vouches";

const RELATIONSHIP_OPTIONS = [
  "Worked together",
  "Known professionally",
  "Would recommend",
  "Former colleague",
  "Trade partner",
];

export function VouchButton({
  voucheeId,
  voucheeName,
  alreadyVouched,
}: {
  voucheeId: string;
  voucheeName: string;
  alreadyVouched: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [relationship, setRelationship] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(alreadyVouched);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    const fd = new FormData();
    fd.set("voucheeId", voucheeId);
    fd.set("relationship", relationship);
    fd.set("message", message);

    const result = await createVouch(fd);
    if (result.error) {
      setError(result.error);
      setSubmitting(false);
    } else {
      setDone(true);
      setOpen(false);
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-forest font-medium">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 13l4 4L19 7" />
        </svg>
        Vouched
      </span>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-4 py-2 border border-forest/20 text-forest text-sm font-medium rounded-sm hover:bg-forest/5 transition-colors bg-transparent cursor-pointer"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
        </svg>
        Vouch for {voucheeName}
      </button>

      {open && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-charcoal/40 dark:bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="relative bg-white rounded-md p-6 md:p-8 max-w-md w-full shadow-xl">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-text-muted hover:text-charcoal bg-transparent border-none cursor-pointer"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <h2 className="font-serif text-xl font-bold text-charcoal mb-1">
              Vouch for {voucheeName}
            </h2>
            <p className="text-text-muted text-sm mb-6">
              Your endorsement helps build trust in the Tradesfolk community.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-sm text-sm text-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-body mb-1.5">
                  How do you know them?
                </label>
                <select
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest bg-white"
                >
                  <option value="">Select...</option>
                  {RELATIONSHIP_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-body mb-1.5">
                  Short endorsement
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  maxLength={200}
                  rows={3}
                  placeholder="e.g. Reliable, skilled, and always delivers quality work."
                  className="w-full px-4 py-3 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest resize-none"
                />
                <p className="text-xs text-text-muted mt-1">
                  {message.length}/200
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting || !relationship || !message}
                className="w-full px-6 py-3 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors text-sm disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit Vouch"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
